/**
 * Build chain-of-custody steps for Card Journey timeline.
 * Uses the same step keys as OwnerOrdersFlowRow / ownerOrderFlowSteps.
 */

import { OWNER_FLOW_STEPS, getCurrentStep, isStepDone } from './ownerOrderFlowSteps';
import { formatDateTimeOrNull } from './formatters';

function getStepMeta(order, stepKey) {
  if (!order) return { date: null, txHash: null, blockNumber: null };
  const map = {
    quote: { date: order.createdAt },
    payment: { date: order.paidAt },
    ship_to_grader: { date: order.shippingSentAt || order.shippedByOwnerAt },
    received: { date: order.cardReceivedAt },
    grading: { date: order.gradingStartedAt },
    graded: { date: order.gradingCompletedAt || order.completedAt },
    sent_to_lab: { date: order.shippedToLabAt },
    received_by_mint_lab: { date: order.labReceivedAt },
    ready_for_minting: { date: order.completedAt },
    in_progress: { date: order.updatedAt },
    minted: { date: order.mintedAt, txHash: order.nftTxHash },
    nfc_info_added: { date: order.nfcInfoAddedAt },
    sent_to_owner: { date: order.shippedToOwnerAt },
    received_by_owner: { date: order.deliveredAt },
  };
  const m = map[stepKey] || {};
  return {
    date: formatDateTimeOrNull(m.date),
    txHash: m.txHash || null,
    blockNumber: null,
  };
}

/**
 * @returns {Array<{ stepKey: string, stepName: string, icon: string, status: 'done'|'active'|'pending', date: string|null, txHash: string|null, blockNumber: string|null }>}
 */
export function buildChainOfCustodySteps(card, order) {
  const currentKey = getCurrentStep(order);
  const curIdx = OWNER_FLOW_STEPS.findIndex((s) => s.key === currentKey);
  const safeIdx = curIdx >= 0 ? curIdx : 0;

  return OWNER_FLOW_STEPS.map((step, i) => {
    let status;
    if (order?.status === 'CANCELLED') {
      status = isStepDone(order, step.key) ? 'done' : 'pending';
    } else if (i < safeIdx) {
      status = 'done';
    } else if (i === safeIdx) {
      status = 'active';
    } else {
      status = 'pending';
    }

    const meta = getStepMeta(order, step.key);

    return {
      stepKey: step.key,
      stepName: step.label,
      icon: step.icon,
      status,
      date: meta.date,
      txHash: meta.txHash,
      blockNumber: meta.blockNumber,
    };
  });
}

/**
 * Normalize API timeline payload when backend implements GET /cards/:id/timeline
 */
export function normalizeApiTimelinePayload(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((row, i) => {
    const rawStatus = row.status;
    const normalizedStatus =
      rawStatus === 'completed'
        ? 'done'
        : ['done', 'active', 'pending'].includes(rawStatus)
          ? rawStatus
          : 'pending';
    return {
    stepKey: row.stepKey || row.stepName || `step-${i}`,
    stepName: row.stepName || row.label || 'Step',
    icon: row.icon || '•',
    status: normalizedStatus,
    date:
      row.date != null
        ? formatDateTimeOrNull(row.date) ?? (typeof row.date === 'string' ? row.date : null)
        : null,
    txHash: row.txHash || null,
    blockNumber: row.blockNumber != null ? String(row.blockNumber) : null,
  };
  });
}
